import type { PayloadAction }  from "@reduxjs/toolkit";
import { createSlice }         from "@reduxjs/toolkit";
import { UserTypeEnum }        from "src/store/services/admin/roles/interfaces/responses.interface";
import type { ILoginResponse } from "src/store/services/auth/interfaces/responses.interface";

type AuthState = {
  user           : ILoginResponse["user"] | null;
  isAuthenticated: boolean;
  isLoading      : boolean;
  error          : string | null;
}

const initialState: AuthState = {
  user           : null,
  isAuthenticated: false,
  isLoading      : false,
  error          : null,
};

const authSlice = createSlice({
  name    : "auth",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<ILoginResponse["user"]>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.error = null;
    },
    clearUser: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    // Login action
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<Partial<ILoginResponse>>) => {
      state.isLoading = false;
      state.user = {
        id   : action.payload.user?.id || state.user?.id || 0,
        name : action.payload.user?.name || state.user?.name || "",
        email: action.payload.user?.email || state.user?.email || "",
        role : action.payload.user?.role || state.user?.role || {
          id         : 0,
          userType   : UserTypeEnum.REGULAR,
          permissions: {}
        },
        ...state.user,
        ...action.payload.user,
      };
      state.isAuthenticated = true;
      state.error = null;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.isAuthenticated = false;
      state.user = null;
    },
    // Logout action
    logoutStart: (state) => {
      state.isLoading = true;
    },
    logoutSuccess: (state) => {
      state.isLoading = false;
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    logoutFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.user = null;
      state.isAuthenticated = false;
      state.error = action.payload;
    },
  },
});

export const { clearError, 
  clearUser, 
  loginFailure, 
  loginStart, 
  loginSuccess,
  logoutFailure,
  logoutStart,
  logoutSuccess,
  setError,
  setLoading,
  setUser } = authSlice.actions;

export const { reducer: authReducer } = authSlice;